import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePacks } from "../hooks/usePacks";
import { useOffsetSortPagination } from "../hooks/useOffsetSortPagination";
import ServerSideTable from "../components/ServerSideTable";
import { resolveServerSortField, type Column } from "../components/Table";
import FilterChips from "../components/FilterChips";
import Button from "../components/Button";
import type { Pack } from "../lib/types";
import Card from "../components/Card";
import { useRole } from "../hooks/useRole";
import { exportApi } from "../lib/api";
import { Download } from "lucide-react";
import SearchBar from "../components/SearchBar";
import Skeleton from "../components/Skeleton";
import {
  applyListTableToSearchParams,
  initialSortingFromListUrl,
  readListTableSearch,
  readPacksSearchQuery,
  readStatusChipParam,
  searchParamsEffectivelyEqual,
  serverPaginationInitFromListUrl,
} from "../lib/listUrlParams";

const statusFilters = [
  { id: "all", label: "All", value: "" },
  { id: "idle", label: "Idle", value: "Idle" },
  { id: "deployed", label: "Deployed", value: "Deployed" },
  { id: "decommissioned", label: "Decommissioned", value: "Decommissioned" },
  { id: "scrapped", label: "Scrapped", value: "Scrapped" },
];

const packListColumns: Column<Pack>[] = [
  {
    header: "Pack ID",
    accessor: (pack: Pack) => pack.pack_id,
    mobileLabel: "Pack ID",
  },
  {
    header: "Manufacturer",
    accessor: (pack: Pack) => pack.bp_manufacturer || "-",
    mobileLabel: "Manufacturer",
    sortField: "bp_manufacturer",
  },
  {
    header: "Activation",
    accessor: (pack: Pack) =>
      pack.activation_date
        ? new Date(pack.activation_date).toLocaleDateString()
        : "-",
    mobileLabel: "Activation",
    sortField: "activation_date",
  },
  {
    header: "Sale Date",
    accessor: (pack: Pack) =>
      pack.sale_date
        ? new Date(pack.sale_date).toLocaleDateString()
        : "-",
    mobileLabel: "Sale Date",
  },
  {
    header: "Location",
    accessor: (pack: Pack) => pack.customer_location || "-",
    mobileLabel: "Location",
    sortField: "customer_location",
  },
  {
    header: "Status",
    accessor: (pack: Pack) => (
      <span
        className={`px-2 py-1 rounded-full text-xs font-semibold ${
          pack.status === "Deployed"
            ? "bg-green-100 text-green-800"
            : pack.status === "Scrapped"
              ? "bg-red-100 text-red-800"
              : pack.status === "Decommissioned"
                ? "bg-amber-100 text-amber-800"
                : "bg-blue-100 text-blue-800"
        }`}
      >
        {pack.status || "Idle"}
      </span>
    ),
    mobileLabel: "Status",
  },
];

export default function Packs() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isEditor } = useRole();
  const urlBootRef = useRef<{
    statusFilter: string[];
    searchQuery: string;
    table: ReturnType<typeof readListTableSearch>;
  } | null>(null);
  if (urlBootRef.current === null) {
    const sp = new URLSearchParams(window.location.search);
    urlBootRef.current = {
      statusFilter: readStatusChipParam(sp),
      searchQuery: readPacksSearchQuery(sp),
      table: readListTableSearch(sp),
    };
  }
  const urlBoot = urlBootRef.current;
  const [statusFilter, setStatusFilter] = useState(urlBoot.statusFilter);
  const [searchQuery, setSearchQuery] = useState(urlBoot.searchQuery);

  const resolvePackSortField = useCallback(
    (columnId?: string) => resolveServerSortField(packListColumns, columnId),
    []
  );

  const {
    pagination,
    sorting,
    listQuery,
    onSortingChange,
    onPageSizeChange,
    clearSorting,
    syncCursorLastKey,
    canGoNext,
  } = useOffsetSortPagination(50, {
    resolveSortField: resolvePackSortField,
    initialSorting: initialSortingFromListUrl(
      urlBoot.table.sortId,
      urlBoot.table.orderDesc
    ),
    paginationInit: serverPaginationInitFromListUrl(
      urlBoot.table.page,
      urlBoot.table.sortId,
      urlBoot.table.cursors
    ),
  });

  const { data, isLoading, error } = usePacks({
    status: statusFilter.length > 0 ? statusFilter[0] : undefined,
    search: searchQuery,
    limit: pagination.pageSize,
    ...listQuery,
  });

  useEffect(() => {
    syncCursorLastKey(data?.lastKey);
  }, [data?.lastKey, syncCursorLastKey]);

  useEffect(() => {
    const next = applyListTableToSearchParams(searchParams, {
      page: pagination.currentPage,
      sorting,
      lastKeys: pagination.lastKeys,
    });
    const status = statusFilter[0];
    if (status) next.set("status", status);
    else next.delete("status");
    if (searchQuery) next.set("q", searchQuery);
    else next.delete("q");
    if (!searchParamsEffectivelyEqual(next, searchParams)) {
      setSearchParams(next, { replace: true });
    }
  }, [
    searchParams,
    setSearchParams,
    pagination.currentPage,
    pagination.lastKeys,
    sorting,
    statusFilter,
    searchQuery,
  ]);

  const updateSearch = (query: string) => {
    setSearchQuery(query);
    const newParams = new URLSearchParams(searchParams);
    if (query) {
      newParams.set("q", query);
    } else {
      newParams.delete("q");
    }
    setSearchParams(newParams);
    pagination.reset();
    clearSorting();
  };

  const handlePageChange = (direction: "next" | "prev" | "first" | "last") => {
    if (direction === "next") {
      pagination.nextPage();
    } else if (direction === "prev") {
      pagination.previousPage();
    } else if (direction === "first") {
      pagination.reset();
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl md:text-4xl font-extrabold text-gray-900 leading-tight">
            Battery Packs
          </h1>
          <p className="text-sm md:text-lg text-gray-500 font-medium mt-1">
            Manage and track battery pack inventory
          </p>
        </div>
        <div className="flex flex-wrap gap-2 w-full md:w-auto">
          <Button
            variant="secondary"
            onClick={async () => {
              try {
                await exportApi.download("packs");
              } catch {
                alert("Failed to export packs");
              }
            }}
            className="flex items-center gap-2 md:flex-none"
          >
            <Download className="w-4 h-4 mr-1.5" />
            Export
          </Button>
          {isEditor && (
            <Button
              variant="primary"
              onClick={() => navigate("/packs/new")}
              className="flex-1 md:flex-none shadow-md"
            >
              + Add New Pack
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <SearchBar
              placeholder="Search packs by ID, customer..."
              value={searchQuery}
              onChange={(e) => updateSearch(e.target.value)}
              onClear={() => updateSearch("")}
            />
          </div>
        </div>
        <FilterChips
          filters={statusFilters}
          activeFilters={statusFilter}
          onToggle={(value) => {
            if (value === "") {
              setStatusFilter([]);
            } else {
              setStatusFilter([value]);
              pagination.reset();
            }
            clearSorting();
          }}
          onClear={() => {
            setStatusFilter([]);
            pagination.reset();
            clearSorting();
          }}
        />
      </div>

      {isLoading ? (
        <div className="space-y-4">
          <Card className="p-0 overflow-hidden">
            <div className="space-y-4 p-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex gap-4">
                  <Skeleton className="h-12 w-full" />
                </div>
              ))}
            </div>
          </Card>
        </div>
      ) : error ? (
        <Card>
          <div className="bg-red-50 border-l-4 border-red-500 rounded-xl p-6">
            <div className="flex items-start gap-3">
              <svg
                className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <h4 className="text-sm font-semibold text-red-900 mb-1">
                  Error Loading Data
                </h4>
                <p className="text-sm text-red-700 mb-2">
                  {error instanceof Error
                    ? error.message
                    : "Failed to load packs. Please check if the backend server is running."}
                </p>
              </div>
            </div>
          </div>
        </Card>
      ) : (
        <ServerSideTable
          columns={packListColumns}
          data={data?.items || []}
          onRowClick={(pack) => navigate(`/packs/${pack.pack_id}`)}
          emptyMessage="No packs found"
          total={data?.total}
          currentPage={pagination.currentPage}
          pageSize={pagination.pageSize}
          canGoNext={canGoNext(data?.total ?? 0, data?.lastKey)}
          canGoPrevious={pagination.canGoPrevious}
          onPageChange={handlePageChange}
          onPageSizeChange={onPageSizeChange}
          sorting={sorting}
          onSortingChange={onSortingChange}
        />
      )}
    </div>
  );
}
